import { Printer, X } from "lucide-react";
import { money, fmtDateTime } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { printReceipt } from "../../lib/printer";
import { getPrinterProfile } from "../../lib/printer/profile";

function OrderItems({ items = [] }) {
  return (
    <div className="border-t border-dashed border-gray-300 pt-2 space-y-2">
      {items.map((it, i) => (
        <div key={i}>
          <div className="flex justify-between gap-2">
            <span>{it.qty}× {it.name}</span>
            <span>{money(it.line_total)}</span>
          </div>

          <p className="text-[10px] font-semibold text-gray-600 pl-3">
            {it.order_type || "DINE IN"}
          </p>

          {it.notes && (
            <p className="text-[10px] text-gray-500 pl-3">
              Note: {it.notes}
            </p>
          )}

          {(it.variants?.length > 0 || it.modifiers?.length > 0) && (
            <p className="text-[10px] text-gray-500 pl-3">
              {[
                ...(it.variants || []),
                ...(it.modifiers || []).map((m) => m.name),
              ].join(", ")}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function TotalsBody({ order }) {
  return (
    <div className="border-t border-dashed border-gray-300 pt-2 space-y-0.5">
      <div className="flex justify-between">
        <span>Subtotal</span>
        <span>{money(order.subtotal)}</span>
      </div>

      {order.discount && (
        <div className="flex justify-between">
          <span>Discount ({order.discount.label})</span>
          <span>-{money(order.discount.amount)}</span>
        </div>
      )}

      {order.tax && (
        <div className="flex justify-between">
          <span>
            {order.tax.name} {order.tax.percent}%
            {order.tax.inclusive ? " (incl)" : ""}
          </span>
          <span>{money(order.tax.amount)}</span>
        </div>
      )}

      {order.service_charge && (
        <div className="flex justify-between">
          <span>
            {order.service_charge.name} {order.service_charge.percent}%
          </span>
          <span>{money(order.service_charge.amount)}</span>
        </div>
      )}

      <div className="flex justify-between font-bold text-sm border-t border-dashed border-gray-300 pt-1">
        <span>TOTAL</span>
        <span>{money(order.grand_total)}</span>
      </div>
    </div>
  );
}

export function ReceiptBody({ order, brand }) {
  return (
    <div
      className="receipt-print bg-[#FAF8F5] text-[#111] font-mono text-xs p-6 rounded-lg max-w-sm mx-auto border border-amber-200 space-y-3"
      data-testid="receipt-view"
    >
      <div className="text-center space-y-0.5">
        <p className="font-bold text-sm">{brand}</p>
        <p className="font-semibold">{order.outlet?.name}</p>
        <p>{order.outlet?.address}</p>
      </div>

      {order.bill_type === "SELF_ORDER" && (
        <div className="border-t border-dashed border-gray-300 pt-2 space-y-0.5">
          <p className="font-bold">MEJA</p>
          <p>{order.self_order?.table_name || "—"}</p>
          {order.self_order?.table_code && (
            <p>{order.self_order.table_code}</p>
          )}
        </div>
      )}

      <div className="border-t border-dashed border-gray-300 pt-2 space-y-0.5">
        <p className="break-all">{order.transaction_number}</p>
        <p>{fmtDateTime(order.created_at)}</p>
        {order.customer_name && (
          <p>
            Customer: <strong>{order.customer_name}</strong>
          </p>
        )}
        <p>
          Cashier: {order.cashier?.name} ({order.cashier?.code})
        </p>
      </div>

      <OrderItems items={order.items} />

      <TotalsBody order={order} />

      <div className="flex justify-between">
        <span>{order.payment?.method_name}</span>
        <span>{money(order.payment?.amount_paid)}</span>
      </div>

      {order.payment?.change > 0 && (
        <div className="flex justify-between">
          <span>Change</span>
          <span>{money(order.payment.change)}</span>
        </div>
      )}

      {order.receipt_footer && (
        <p className="text-center border-t border-dashed border-gray-300 pt-2 text-gray-600">
          {order.receipt_footer}
        </p>
      )}
    </div>
  );
}

export function OpenBillBody({ order, brand }) {
  const batches =
    Array.isArray(order.order_batches) && order.order_batches.length > 0
      ? order.order_batches
      : [
          {
            id: "legacy",
            sequence: 1,
            created_at: order.created_at,
            created_by: order.cashier,
            items: order.items || [],
            subtotal: order.subtotal || 0,
            discount: order.discount?.amount || 0,
            tax: order.tax?.amount || 0,
            service_charge: order.service_charge?.amount || 0,
            grand_total: order.grand_total || 0,
          },
        ];

  return (
    <div
      className="receipt-print bg-[#FAF8F5] text-[#111] font-mono text-xs p-6 rounded-lg max-w-sm mx-auto border border-amber-200 space-y-3"
      data-testid="open-bill-view"
    >
      <div className="text-center space-y-0.5">
        <p className="font-bold text-sm">{brand}</p>
        <p className="font-semibold">{order.outlet?.name}</p>
        <p>{order.outlet?.address}</p>
        <p className="font-bold text-sm mt-2">OPEN BILL / INVOICE</p>
        <p className="font-bold text-red-700">OUTSTANDING / BELUM DIBAYAR</p>
      </div>

      <div className="border-t border-dashed border-gray-300 pt-2 space-y-0.5">
        <p className="break-all">{order.transaction_number}</p>

        {order.customer_name && (
          <p>
            Customer: <strong>{order.customer_name}</strong>
          </p>
        )}

        <p>Opening: {fmtDateTime(order.created_at)}</p>

        {order.updated_at && (
          <p>Last Update: {fmtDateTime(order.updated_at)}</p>
        )}

        <p>
          Opened by: {order.opened_by?.name || order.cashier?.name}
          {(order.opened_by?.code || order.cashier?.code)
            ? ` (${order.opened_by?.code || order.cashier?.code})`
            : ""}
        </p>

        {order.status === "PAID" ? (
          <p>
            Closed by:{" "}
            <strong>
              {order.payment?.paid_by_name ||
                order.last_updated_by?.name ||
                "—"}
            </strong>
          </p>
        ) : (
          batches.length > 1 &&
          batches[batches.length - 1]?.created_by && (
            <p>
              Added by:{" "}
              <strong>
                {batches[batches.length - 1].created_by.name || "—"}
              </strong>
              {batches[batches.length - 1].created_by.code
                ? ` (${batches[batches.length - 1].created_by.code})`
                : ""}
            </p>
          )
        )}
      </div>

      {batches.map((batch, index) => (
        <div
          key={batch.id || index}
          className="border-t border-dashed border-gray-300 pt-2 space-y-2"
        >
          <div className="font-bold">
            DAY / ORDER #{batch.sequence || index + 1}
          </div>

          <p className="text-[10px] text-gray-600">
            {fmtDateTime(batch.created_at)}
          </p>

          <OrderItems items={batch.items || []} />

          <div className="pt-1 space-y-0.5">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{money(batch.subtotal || 0)}</span>
            </div>

            {(batch.discount || 0) > 0 && (
              <div className="flex justify-between">
                <span>Discount</span>
                <span>-{money(batch.discount)}</span>
              </div>
            )}

            {(batch.tax?.amount || 0) > 0 && (
              <div className="flex justify-between">
                <span>
                  {batch.tax?.name || "Tax"}
                  {batch.tax?.percent != null ? ` ${batch.tax.percent}%` : ""}
                </span>
                <span>{money(batch.tax.amount)}</span>
              </div>
            )}

            {(batch.service_charge?.amount || 0) > 0 && (
              <div className="flex justify-between">
                <span>
                  {batch.service_charge?.name || "Service"}
                  {batch.service_charge?.percent != null
                    ? ` ${batch.service_charge.percent}%`
                    : ""}
                </span>
                <span>{money(batch.service_charge.amount)}</span>
              </div>
            )}

            <div className="flex justify-between font-bold border-t border-dashed border-gray-300 pt-1">
              <span>DAY TOTAL</span>
              <span>{money(batch.grand_total || 0)}</span>
            </div>
          </div>
        </div>
      ))}

      <div className="border-t-2 border-gray-700 pt-2 space-y-1">
        <div className="flex justify-between font-bold text-sm">
          <span>GRAND TOTAL</span>
          <span>{money(order.grand_total)}</span>
        </div>

        <div className="flex justify-between font-bold text-sm text-red-700">
          <span>OUTSTANDING</span>
          <span>{money(order.grand_total)}</span>
        </div>
      </div>

      <p className="text-center border-t border-dashed border-gray-300 pt-2 text-gray-600">
        This is an invoice / open bill.
        <br />
        Payment has not been completed.
      </p>
    </div>
  );
}

export default function ReceiptModal({ order, onClose }) {
  const { session } = useAuth();

  if (!order) return null;

  const isOpenBill =
    order.status === "OPEN" ||
    order.bill_type === "OPEN" ||
    order.open_bill === true;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="receipt-modal"
      >
        <div
          className={`rounded-xl p-3 text-center ${
            isOpenBill
              ? "bg-amber-500/15 border border-amber-500/30"
              : "bg-emerald-500/15 border border-emerald-500/30"
          }`}
        >
          <p
            className={`font-bold ${
              isOpenBill ? "text-amber-400" : "text-emerald-400"
            }`}
            data-testid="receipt-success-label"
          >
            {isOpenBill ? "OPEN BILL" : "Transaction Complete"}
          </p>

          <p
            className={`font-mono text-xs break-all ${
              isOpenBill ? "text-amber-300/80" : "text-emerald-300/80"
            }`}
          >
            {order.transaction_number}
          </p>
        </div>

        {isOpenBill ? (
          <OpenBillBody
            order={order}
            brand={session?.tenant?.brand_name || "GLOO POS"}
          />
        ) : (
          <ReceiptBody
            order={order}
            brand={session?.tenant?.brand_name || "GLOO POS"}
          />
        )}

        <div className="flex gap-2">
          <button
            onClick={() =>
              printReceipt({
                order,
                documentType: isOpenBill ? "open_bill" : "receipt",
                printer: getPrinterProfile(),
              })
            }
            data-testid={isOpenBill ? "invoice-print-button" : "receipt-print-button"}
            className="flex-1 h-11 rounded-xl bg-secondary border border-border font-bold text-sm flex items-center justify-center gap-2 hover:bg-accent active:scale-[0.98]"
          >
            <Printer size={16} />
            {isOpenBill ? "Print Invoice" : "Print Receipt"}
          </button>

          <button
            onClick={onClose}
            data-testid="receipt-done-button"
            className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98]"
          >
            <X size={16} /> New Sale
          </button>
        </div>
      </div>
    </div>
  );
}
